/**
 * Select Component
 * Accessible dropdown select with validation
 */

import { SelectHTMLAttributes, forwardRef } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Select label */
  label?: string;

  /** Select options */
  options: SelectOption[];

  /** Error message */
  error?: string;

  /** Helper text */
  helperText?: string;

  /** Placeholder text */
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      options,
      error,
      helperText,
      placeholder = 'Select an option',
      className = '',
      id,
      required,
      disabled,
      ...props
    },
    ref
  ) => {
    const selectId = id || `select-${label?.toLowerCase().replace(/\s+/g, '-')}`;

    // Base styles
    const baseStyles = 'ww-block ww-w-full ww-rounded-md ww-shadow-sm focus:ww-ring-2 focus:ww-ring-primary focus:ww-border-primary ww-transition-colors ww-px-3 ww-py-2 ww-pr-10';

    // State styles
    const stateStyles = error
      ? 'ww-border-red-300 ww-text-red-900 focus:ww-ring-red-500 focus:ww-border-red-500'
      : 'ww-border-gray-300 ww-text-gray-900';

    // Disabled styles
    const disabledStyles = disabled ? 'ww-bg-gray-100 ww-cursor-not-allowed' : 'ww-bg-white';

    const selectClasses = `${baseStyles} ${stateStyles} ${disabledStyles} ${className}`;

    return (
      <div className="ww-w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1"
          >
            {label}
            {required && <span className="ww-text-red-500 ww-ml-1">*</span>}
          </label>
        )}

        <select
          ref={ref}
          id={selectId}
          className={selectClasses}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined
          }
          disabled={disabled}
          required={required}
          {...props}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>

        {error && (
          <p
            id={`${selectId}-error`}
            className="ww-mt-1 ww-text-sm ww-text-red-600"
            role="alert"
          >
            {error}
          </p>
        )}

        {!error && helperText && (
          <p id={`${selectId}-helper`} className="ww-mt-1 ww-text-sm ww-text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
